import axios from "axios";

function getApiBaseUrl() {
  return process.env.REACT_APP_API_URL || "";
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken");
  return token
    ? { accept: "*/*", Authorization: `Bearer ${token}` }
    : { accept: "*/*" };
}

/**
 * @param {import('../types/adminEditBooking.types').TransitionCostPreviewRequest[]} requests
 */
export async function previewTransitionCost(requests) {
  const { data } = await axios.post(
    `${getApiBaseUrl()}TransitionCost/GetTotalTransitionCost`,
    requests,
    { headers: { "Content-Type": "application/json", ...getAuthHeaders() } },
  );
  return Array.isArray(data) ? data : [data];
}

/**
 * @param {import('../types/adminEditBooking.types').AdminEditBookingRequest} payload
 * @returns {Promise<import('../types/adminEditBooking.types').AdminEditBookingResponse>}
 */
export async function adminEditBooking(payload) {
  const { data } = await axios.put(
    `${getApiBaseUrl()}TransitionCost/AdminEditBooking`,
    payload,
    { headers: { "Content-Type": "application/json", ...getAuthHeaders() } },
  );
  return data;
}

/**
 * @param {number|string} parcelId
 * @returns {Promise<import('../types/adminEditBooking.types').ParcelPaymentRow[]>}
 */
export async function fetchPaymentsByParcelId(parcelId) {
  const { data } = await axios.get(
    `${getApiBaseUrl()}Payment/GetPaymentsByParcelId/${parcelId}`,
    { headers: getAuthHeaders() },
  );
  return Array.isArray(data) ? data : [];
}

/**
 * @param {number|string} paymentId
 * @param {{ modifierId: string, settlementNote?: string }} body
 */
export async function settleCreditPayment(paymentId, body) {
  const { data } = await axios.put(
    `${getApiBaseUrl()}Payment/SettleCredit/${paymentId}`,
    body,
    { headers: { "Content-Type": "application/json", ...getAuthHeaders() } },
  );
  return data;
}

/**
 * @param {number|string} paymentId
 * @param {object} [body]
 */
export async function verifyManualBankPayment(paymentId, body = {}) {
  const { data } = await axios.put(
    `${getApiBaseUrl()}Payment/VerifyManualBankPayment/${paymentId}`,
    body,
    { headers: { "Content-Type": "application/json", ...getAuthHeaders() } },
  );
  return data;
}

/**
 * Payment-centric parcel summary (invoice / payment method). Not product lines.
 * @param {number|string} parcelNo
 */
export async function fetchParcelDetailsByParcelNo(parcelNo) {
  const { data } = await axios.get(
    `${getApiBaseUrl()}ParcelDetails/GetParcelDetailsByParcelNo/${parcelNo}`,
    { headers: getAuthHeaders() },
  );
  return data;
}

/**
 * Saved parcel product lines for remesaure prefill (item type, category, ids, qty, value).
 * Measured L/W/H are not stored on the booking — only dimension/weight band ids.
 * @param {number|string} parcelId
 */
export async function fetchParcelProductsForEdit(parcelId) {
  const { data } = await axios.get(
    `${getApiBaseUrl()}TransitionCost/GetParcelProductsForEdit/${parcelId}`,
    { headers: getAuthHeaders() },
  );
  return Array.isArray(data) ? data : [];
}

/**
 * @param {number|string} parcelNo
 */
export async function fetchParcelCostDetails(parcelNo) {
  const { data } = await axios.get(
    `${getApiBaseUrl()}ParcelDetails/GetParcelCostDetails?parcelNo=${parcelNo}`,
    { headers: getAuthHeaders() },
  );
  return data || {};
}

/**
 * Find one parcel from booking history (includes receiver/sender fields).
 * @param {number|string} parcelNo
 */
export async function fetchParcelBookingByParcelNo(parcelNo) {
  if (parcelNo == null || parcelNo === "") return null;
  try {
    const { data } = await axios.get(
      `${getApiBaseUrl()}ParcelBookingInformation/GetParcelHistory`,
      { headers: getAuthHeaders() },
    );
    const rows = Array.isArray(data) ? data : [];
    return (
      rows.find(
        (p) =>
          String(p.parcelNo) === String(parcelNo) ||
          String(p.parcelId) === String(parcelNo),
      ) || null
    );
  } catch {
    return null;
  }
}
